import { type ReactNode } from "react";
import {
  type DefaultValues,
  type FieldValues,
  type RegisterOptions,
  useForm,
} from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PageHeader } from "@/components/v2/page-header";
import type { EntityFieldConfig } from "@/components/v2/entity-form-config";

/**
 * ADV-787: the app's first create/edit PAGE pattern. A config-driven scaffold
 * over react-hook-form + the shadcn Form/Field primitives so each entity's
 * create/edit page is a field config rather than a bespoke form (the dominant
 * in-app pattern before this was the right-side Sheet — see
 * proposal-form-sheet.tsx). Token-driven per ADR-0018; operator-grade voice per
 * ADR-0019 §5 (verb+object buttons, no exclamation). Field-config types and the
 * enumOptions helper live in ./entity-form-config so this file only exports a
 * component (react-refresh).
 *
 * Validation is RHF field-level (required + email format) rather than a zod
 * resolver: @adva/types is zod v3 while apps/web is zod v4, so the server-side
 * insert schemas cannot be reused as a client resolver. The server remains the
 * authoritative validator; the client checks are for fast UX feedback.
 */

interface EntityFormProps<T extends FieldValues> {
  /** Page H1, e.g. "Create customer" / "Edit customer". */
  title: string;
  subtitle?: string;
  breadcrumbs: { label: string; href?: string }[];
  defaultValues: DefaultValues<T>;
  fields: EntityFieldConfig<T>[];
  /** Verb+object submit label, e.g. "Create customer" / "Save changes". */
  submitLabel: string;
  /** Receives the form values with empty strings stripped to undefined. */
  onSubmit: (values: T) => Promise<void>;
  /** Where Cancel navigates to (e.g. "/customers"). */
  cancelHref: string;
  /** Inline submit error, surfaced as a destructive Alert above the form. */
  error?: string | null;
  isSubmitting?: boolean;
}

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function fieldRules<T extends FieldValues>(
  cfg: EntityFieldConfig<T>,
): RegisterOptions<T> {
  const rules: RegisterOptions<T> = {};
  if (cfg.required) rules.required = `${cfg.label} is required`;
  if (cfg.control === "email") {
    // A `validate` fn (rather than `pattern`) reliably runs against the
    // controlled value and tolerates an empty optional email.
    rules.validate = (value: unknown) =>
      !value ||
      EMAIL_PATTERN.test(String(value)) ||
      "Enter a valid email address";
  }
  return rules;
}

/** Strip empty-string and undefined fields so optional values are omitted from the payload. */
function clean<T extends FieldValues>(values: T): T {
  return Object.fromEntries(
    Object.entries(values).filter(([, v]) => v !== "" && v !== undefined),
  ) as T;
}

export function EntityForm<T extends FieldValues>({
  title,
  subtitle,
  breadcrumbs,
  defaultValues,
  fields,
  submitLabel,
  onSubmit,
  cancelHref,
  error,
  isSubmitting,
}: EntityFormProps<T>) {
  const navigate = useNavigate();
  const form = useForm<T>({ defaultValues });

  return (
    <div className="max-w-2xl">
      <PageHeader title={title} subtitle={subtitle} breadcrumbs={breadcrumbs} />
      <Card>
        <CardContent className="pt-6">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form
              data-testid="entity-form"
              onSubmit={form.handleSubmit((values) => onSubmit(clean(values)))}
              className="space-y-5"
            >
              {fields.map((cfg) => (
                <FormField
                  key={cfg.name}
                  control={form.control}
                  name={cfg.name}
                  rules={fieldRules(cfg)}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {cfg.label}
                        {cfg.required && (
                          <span className="text-destructive"> *</span>
                        )}
                      </FormLabel>
                      <FormControl>{renderControl(cfg, field)}</FormControl>
                      {cfg.description && (
                        <FormDescription>{cfg.description}</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ))}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(cancelHref)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : submitLabel}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}

// RHF's `field` is typed against the form value union; the control narrows by
// `cfg.control`. The `field.value` coercions are the standard RHF<->native-input
// interop (string/number/boolean).
function renderControl<T extends FieldValues>(
  cfg: EntityFieldConfig<T>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  field: any,
): ReactNode {
  switch (cfg.control) {
    case "textarea":
      return (
        <Textarea
          {...field}
          value={field.value ?? ""}
          rows={3}
          className="resize-none"
          placeholder={cfg.placeholder}
        />
      );
    case "select":
      return (
        <NativeSelect {...field} value={field.value ?? ""}>
          {!cfg.required && (
            <NativeSelectOption value="">
              {cfg.emptyOptionLabel ?? "—"}
            </NativeSelectOption>
          )}
          {(cfg.options ?? []).map((opt) => (
            <NativeSelectOption key={opt.value} value={opt.value}>
              {opt.label}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      );
    case "switch":
      return (
        <div className="flex h-9 items-center">
          <Switch
            checked={!!field.value}
            onCheckedChange={field.onChange}
            ref={field.ref}
          />
        </div>
      );
    case "number":
      return (
        <Input
          {...field}
          type="number"
          value={field.value ?? ""}
          placeholder={cfg.placeholder}
          onChange={(e) =>
            field.onChange(
              e.target.value === "" ? undefined : e.target.valueAsNumber,
            )
          }
        />
      );
    default:
      return (
        <Input
          {...field}
          type={cfg.control}
          value={field.value ?? ""}
          placeholder={cfg.placeholder}
        />
      );
  }
}
